import React, { useEffect } from "react";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";

export default function CartDrawer() {
  const {
    isOpen,
    close,
    items,
    setQty,
    removeItem,
    subtotal,
    incrementQuantity,
    decrementQuantity,
  } = useCart();
  const { language } = useLanguage();

  // Helper function to add language prefix to paths
  const getLocalizedPath = (path) => {
    if (language === "ar") {
      const cleanPath = path.startsWith("/ar") ? path.replace("/ar", "") : path;
      return `/ar${cleanPath === "/" ? "" : cleanPath}`;
    }
    return path.startsWith("/ar") ? path.replace("/ar", "") || "/" : path;
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isOpen) close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, close]);
  return (
    <>
      <style>
        {`
          /* Enhanced Cart Drawer Styling */
          .offcanvas {
            box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15) !important;
          }

          .offcanvas-backdrop {
            background: rgba(0, 0, 0, 0.5) !important;
            backdrop-filter: blur(4px);
          }

          .offcanvas-header {
            border-bottom: 2px solid #f0f0f0;
            padding: 20px 24px;
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          }

          .offcanvas-title {
            font-size: 24px;
            font-weight: 700;
            color: #1a1a1a;
            letter-spacing: -0.5px;
          }

          .offcanvas-body {
            padding: 20px 24px;
            background: #fafafa;
          }

          .offcanvas-footer {
            background: #ffffff;
            box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
          }

          /* Empty cart state */
          .offcanvas-body p.text-center {
            padding: 60px 20px;
            color: #888;
            font-size: 16px;
          }

          /* Table styling */
          .offcanvas .tf-table-page-cart {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
          }

          .offcanvas .tf-table-page-cart thead th {
            background: transparent;
            padding: 16px 12px;
            font-weight: 600;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #666;
            border-bottom: 2px solid #e8e8e8;
          }

          .offcanvas .tf-table-page-cart tbody tr {
            background: #ffffff;
            border-radius: 12px;
            margin-bottom: 12px;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            border: 1px solid transparent;
          }

          .offcanvas .tf-table-page-cart tbody tr:hover {
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
            transform: translateY(-2px);
            border-color: #e8e8e8;
          }

          .offcanvas .tf-table-page-cart td {
            vertical-align: middle;
            padding: 16px 12px;
            border-bottom: 1px solid #f0f0f0;
          }

          .offcanvas .tf-table-page-cart tbody tr:last-child td {
            border-bottom: none;
          }

          /* Product image styling */
          .offcanvas .tf-table-page-cart img {
            border-radius: 10px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;
          }

          .offcanvas .tf-table-page-cart img:hover {
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          /* Product name styling */
          .offcanvas .tf-table-page-cart .fw-6 {
            font-weight: 600;
            color: #1a1a1a;
            font-size: 15px;
            line-height: 1.5;
          }

          .offcanvas .tf-table-page-cart .text_caption-1 {
            color: #888;
            font-size: 13px;
            margin-top: 4px;
          }

          /* Quantity editor styling */
          .offcanvas .wg-quantity {
            display: inline-flex;
            align-items: center;
            background: #f8f9fa;
            border-radius: 8px;
            padding: 4px;
            border: 1px solid #e0e0e0;
            transition: all 0.2s ease;
          }

          .offcanvas .wg-quantity:hover {
            border-color: #0e5aa7;
            background: #ffffff;
          }

          .offcanvas .btn-quantity {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 18px;
            font-weight: 600;
            color: #0e5aa7;
            transition: all 0.2s ease;
            user-select: none;
          }

          .offcanvas .btn-quantity:hover {
            background: #0e5aa7;
            color: #ffffff;
            transform: scale(1.1);
          }

          .offcanvas .btn-quantity:active {
            transform: scale(0.95);
          }

          .offcanvas .wg-quantity input {
            width: 40px;
            text-align: center;
            border: none;
            background: transparent;
            font-weight: 600;
            font-size: 15px;
            color: #1a1a1a;
            padding: 0;
          }

          /* Remove button styling */
          .offcanvas .link--danger.button-reset {
            padding: 8px !important;
            min-width: 36px !important;
            width: 36px !important;
            height: 36px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: #fee !important;
            border-radius: 8px !important;
            border: 1px solid #fcc !important;
            transition: all 0.2s ease !important;
            cursor: pointer;
          }

          .offcanvas .link--danger.button-reset:hover {
            background: #fdd !important;
            border-color: #f99 !important;
            transform: scale(1.1);
          }

          .offcanvas .link--danger.button-reset:active {
            transform: scale(0.95);
          }

          .offcanvas .link--danger.button-reset span {
            display: none !important;
          }

          .offcanvas .link--danger.button-reset svg {
            width: 18px !important;
            height: 18px !important;
            color: #d32f2f !important;
          }

          /* Total price styling in Qty column */
          .offcanvas .tf-table-page-cart td:nth-child(2) .fw-6 {
            display: block;
          }

          .offcanvas .d-flex.gap-8 {
            gap: 12px;
          }

          /* Footer styling */
          .offcanvas-footer {
            padding: 24px !important;
          }

          .offcanvas-footer .fw-6 {
            font-size: 18px;
            color: #1a1a1a;
          }

          .offcanvas-footer .fw-6:last-child {
            color: #0e5aa7;
            font-size: 20px;
          }

          .offcanvas-footer .tf-btn {
            padding: 14px 24px;
            font-weight: 600;
            font-size: 16px;
            border-radius: 10px;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(14, 90, 167, 0.2);
          }

          .offcanvas-footer .tf-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(14, 90, 167, 0.3);
          }

          @media (max-width: 767px) {
            .offcanvas-header {
              padding: 16px 20px;
            }

            .offcanvas-title {
              font-size: 20px;
            }

            .offcanvas-body {
              padding: 16px 20px;
            }

            /* Convert table to block layout on mobile */
            .offcanvas .tf-table-page-cart {
              display: block;
            }

            .offcanvas .tf-table-page-cart thead {
              display: none;
            }

            .offcanvas .tf-table-page-cart tbody {
              display: block;
            }

            .offcanvas .tf-table-page-cart tr {
              display: block;
              margin-bottom: 12px;
              padding: 12px;
              border-radius: 12px;
              background: #ffffff;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            }

            .offcanvas .tf-table-page-cart td {
              display: block;
              width: 100% !important;
              padding: 8px 0;
              border: none;
              vertical-align: top;
            }

            .offcanvas .tf-table-page-cart td:first-child {
              padding-bottom: 10px;
            }

            .offcanvas .tf-table-page-cart td:nth-child(2) {
              padding-top: 0;
              padding-bottom: 0;
            }

            /* Total price in Qty column on mobile */
            .offcanvas .tf-table-page-cart td:nth-child(2) .fw-6 {
              display: block;
              margin-top: 8px;
              padding-top: 8px;
              border-top: 1px solid #f0f0f0;
              margin-bottom: 0;
              padding-bottom: 0;
            }

            /* Product column - ensure full width and proper spacing */
            .offcanvas .tf-table-page-cart td:first-child .d-flex {
              align-items: flex-start;
            }

            .offcanvas .tf-table-page-cart td:first-child .fw-6 {
              word-wrap: break-word;
              word-break: break-word;
              line-height: 1.4;
              margin-bottom: 4px;
            }

            /* Qty column - ensure controls are properly displayed */
            .offcanvas .tf-table-page-cart td:nth-child(2) {
              margin-top: 4px;
            }

            /* Reduce gap in qty editor container */
            .offcanvas .tf-table-page-cart td:nth-child(2) > div {
              gap: 8px;
            }

            /* Reduce gap in qty editor row */
            .offcanvas .tf-table-page-cart td:nth-child(2) .d-flex {
              gap: 8px;
            }

            .offcanvas-footer {
              padding: 20px !important;
            }
          }
        `}
      </style>
      {isOpen && (
        <div
          className="offcanvas-backdrop fade show"
          onClick={close}
          aria-hidden="true"
        ></div>
      )}
      <div
        className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
        style={{ visibility: isOpen ? "visible" : "hidden" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">{language === "ar" ? "سلة التسوق" : "Your Cart"}</h5>
          <button type="button" className="btn-close" onClick={close}></button>
        </div>
        <div className="offcanvas-body">
          {items.length === 0 ? (
            <div className="text-center" style={{ padding: "60px 20px" }}>
              <div
                style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.3 }}
              >
                🛒
              </div>
              <p
                style={{
                  fontSize: "18px",
                  color: "#888",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                {language === "ar" ? "سلة التسوق فارغة" : "Your cart is empty"}
              </p>
              <p style={{ fontSize: "14px", color: "#aaa" }}>
                {language === "ar" ? "أضف بعض العناصر للبدء" : "Add some items to get started"}
              </p>
            </div>
          ) : (
            <div className="tf-page-cart-item">
              <table className="tf-table-page-cart">
                <thead>
                  <tr>
                    <th>{language === "ar" ? "المنتج" : "Product"}</th>
                    <th>{language === "ar" ? "الكمية" : "Qty"}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id}>
                      <td>
                        <div className="d-flex align-items-start gap-12">
                          <img
                            src={it.image}
                            alt={it.name}
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "cover",
                              flexShrink: 0,
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              className="fw-6 mb-1"
                              style={{
                                wordWrap: "break-word",
                                lineHeight: "1.4",
                              }}
                            >
                              {it.name}
                            </div>
                            <div className="text_caption-1">
                              {it.color || "-"}
                              {it.size ? ` / ${it.size}` : ""}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          <div className="d-flex align-items-center gap-8">
                            <div className="wg-quantity">
                              <span
                                className="btn-quantity"
                                onClick={() => decrementQuantity(it.id)}
                              >
                                -
                              </span>
                              <input
                                type="text"
                                value={Number(it.qty || 1)}
                                readOnly
                              />
                              <span
                                className="btn-quantity"
                                onClick={() => incrementQuantity(it.id)}
                              >
                                +
                              </span>
                            </div>
                            <button
                              type="button"
                              className="link link--danger button-reset"
                              onClick={() => removeItem(it.id)}
                              aria-label={language === "ar" ? "إزالة العنصر" : "Remove item"}
                            >
                              <svg
                                className="link__icon"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <path
                                  d="M3 6h18M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-8 0h8m-7 4 1 10m6-10-1 10M5 6l1.5 16a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2L19 6"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span>{language === "ar" ? "إزالة" : "Remove"}</span>
                            </button>
                          </div>
                          <div
                            className="fw-6"
                            style={{
                              fontSize: "16px",
                              color: "#0e5aa7",
                              marginTop: "8px",
                              fontWeight: 700,
                            }}
                          >
                            {((it.price || 0) * (it.qty || 1)).toFixed(2)} EGP
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="offcanvas-footer p-3 border-top">
          <div className="d-flex justify-content-between mb-3">
            <div className="fw-6">{language === "ar" ? "المجموع الفرعي" : "Subtotal"}</div>
            <div className="fw-6">{(subtotal || 0).toFixed(2)} EGP</div>
          </div>
          <a
            href={getLocalizedPath("/cart")}
            className="tf-btn btn-fill w-100 justify-content-center"
          >
            {language === "ar" ? "عرض السلة" : "View Cart"}
          </a>
        </div>
      </div>
    </>
  );
}
